import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Badge, Select, Input, Button } from '../components/ui';
import { PageHeader } from '../components/PageHeader';
import { AggregatorLinksCard } from '../components/AggregatorLinksCard';
import { ComputedBadges } from '../components/ComputedBadges';
import { Monogram } from '../components/Monogram';
import { computeScholarshipBadges } from '../lib/computedBadges';
import { NeedsReviewFlag } from '../components/NeedsReviewFlag';
import { stalenessReasons } from '../lib/staleness';
import { Link } from 'react-router-dom';
import { isPast } from 'date-fns';
import { Star, AlertTriangle, Plus } from 'lucide-react';
import { humanize } from '../lib/humanize';
import { safeParseISO, safeFormatDate } from '../lib/dates';
import type { Scholarship, ScholarshipStatus } from '@scholarship-app/types';

const STATUS_OPTIONS: Array<{ value: ScholarshipStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All Statuses' },
  { value: 'identified', label: 'Identified' },
  { value: 'researching', label: 'Researching' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'awarded', label: 'Awarded' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
];

const STATUS_VARIANTS: Record<string, 'gray' | 'blue' | 'amber' | 'green' | 'red' | 'indigo' | 'purple'> = {
  identified: 'gray',
  researching: 'blue',
  in_progress: 'amber',
  submitted: 'indigo',
  awarded: 'green',
  rejected: 'red',
  expired: 'red',
};

export function ScholarshipsPage() {
  const { membership } = useAuth();
  const { selectedApplicantId } = useApplicantContext();
  const applicantId = selectedApplicantId;
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState<ScholarshipStatus | 'all'>('all');
  const [schoolFilter, setSchoolFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'deadline' | 'amount' | 'name' | 'fit'>('deadline');
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!db || !membership || !applicantId) return;
    const colRef = collection(db, 'households', membership.householdId, 'applicants', applicantId, 'scholarships');
    const unsub = onSnapshot(colRef, (snap) => {
      const list = snap.docs.map((d) => d.data() as Scholarship);
      setScholarships(list);
      setLoading(false);
    });
    return () => unsub();
  }, [membership, applicantId]);

  const filtered = useMemo(() => {
    return scholarships
      .filter((s) => showArchived ? true : !s.archived)
      .filter((s) => (statusFilter === 'all' ? true : s.status === statusFilter))
      .filter((s) => (schoolFilter === 'all' ? true : (s.requires_admission_to ?? '') === schoolFilter))
      .filter((s) => (search ? (s.name + s.sponsor).toLowerCase().includes(search.toLowerCase()) : true))
      .sort((a, b) => {
        if (sortBy === 'deadline') {
          return (a.effective_deadline ?? '9999').localeCompare(b.effective_deadline ?? '9999');
        }
        if (sortBy === 'amount') {
          return (b.amount_max ?? 0) - (a.amount_max ?? 0);
        }
        if (sortBy === 'fit') {
          return (b.fit_score ?? 0) - (a.fit_score ?? 0);
        }
        return a.name.localeCompare(b.name);
      });
  }, [scholarships, statusFilter, schoolFilter, search, sortBy, showArchived]);

  const schoolOptions = useMemo(() => {
    const set = new Set<string>();
    scholarships.forEach((s) => s.requires_admission_to && set.add(s.requires_admission_to));
    return Array.from(set).sort();
  }, [scholarships]);

  if (loading) {
    return <div className="text-soft">Loading scholarships…</div>;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Scholarships"
        description={`${filtered.length} of ${scholarships.length} shown`}
        action={
          <Link to="/scholarships/new">
            <Button>
              <Plus size={16} className="mr-1" />
              Add scholarship
            </Button>
          </Link>
        }
      />

      <AggregatorLinksCard />

      <Card className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-xs text-soft mb-1 block">Search</label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or sponsor" />
        </div>
        <div>
          <label className="text-xs text-soft mb-1 block">Status</label>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ScholarshipStatus | 'all')}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs text-soft mb-1 block">School</label>
          <Select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}>
            <option value="all">All schools / external</option>
            {schoolOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>
        <div>
          <label className="text-xs text-soft mb-1 block">Sort by</label>
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
            <option value="deadline">Deadline (soonest)</option>
            <option value="amount">Amount (highest)</option>
            <option value="fit">Fit score (best)</option>
            <option value="name">Name</option>
          </Select>
        </div>
        <div className="md:col-span-4 flex items-center gap-2 pt-1">
          <input
            id="show-archived"
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <label htmlFor="show-archived" className="text-sm text-soft">
            Show archived scholarships
          </label>
        </div>
      </Card>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <Card className="p-6 text-center text-soft">
            No scholarships match your filters.
          </Card>
        ) : (
          filtered.map((s) => {
            // Defensive: a scholarship may carry an unparseable deadline
            // string (e.g. promoted from an inbox item before v0.2.20.1's
            // promotion fix). safeParseISO returns null rather than an
            // Invalid Date, so a bad value never crashes the render.
            const parsedDeadline = safeParseISO(s.effective_deadline);
            const deadlinePast = parsedDeadline ? isPast(parsedDeadline) : false;
            const deadlineLabel = safeFormatDate(s.effective_deadline, 'MMM d, yyyy');
            const admissionDeadlineLabel = safeFormatDate(s.linked_admission_deadline, 'MMM d');
            return (
              <Link key={s.scholarship_id} to={`/scholarships/${s.scholarship_id}`}>
                <Card className={`p-4 hover:border-forest-300 transition cursor-pointer ${s.archived ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1 flex items-start gap-3">
                      <Monogram name={s.sponsor || s.name} size={38} className="mt-0.5" />
                      <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {s.starred && <Star size={14} className="text-brass-600 fill-brass-600" />}
                        <h3 className="font-medium text-ink truncate">{s.name}</h3>
                        <Badge variant={STATUS_VARIANTS[s.status] ?? 'gray'}>{humanize(s.status)}</Badge>
                        {s.archived && <Badge variant="gray">Archived</Badge>}
                        {s.competitiveness === 'extremely_high' && (
                          <Badge variant="red">Extremely Competitive</Badge>
                        )}
                        <NeedsReviewFlag reasons={stalenessReasons(s)} />
                      </div>
                      <p className="text-sm text-soft mt-0.5">{s.sponsor}</p>
                      <div className="mt-2 flex items-center gap-3 text-xs text-soft flex-wrap">
                        {s.amount_type === 'full_ride' ? (
                          <span className="font-medium text-emerald-700">Full ride</span>
                        ) : s.amount_max ? (
                          <span>${(s.amount_min ?? 0).toLocaleString()}–${s.amount_max.toLocaleString()}</span>
                        ) : (
                          <span className="text-soft">Amount TBD</span>
                        )}
                        {deadlineLabel ? (
                          <span className={deadlinePast ? 'text-rose-600' : ''}>
                            Due {deadlineLabel}
                          </span>
                        ) : s.effective_deadline ? (
                          <span className="text-soft">Due date TBD</span>
                        ) : null}
                        {s.linked_admission_deadline && s.linked_admission_deadline !== s.deadline && admissionDeadlineLabel && (
                          <span className="text-amber-700 flex items-center gap-1">
                            <AlertTriangle size={12} />
                            admission deadline {admissionDeadlineLabel}
                          </span>
                        )}
                        <span>Effort: {humanize(s.effort_estimate)}</span>
                      </div>

                      {/* v0.2.31: computed badges — auto-derived from this
                          scholarship's data. Profile is not loaded on this
                          list page, so the best-effort "Local" badge is
                          omitted here (it needs the applicant's state). */}
                      <div className="mt-2">
                        <ComputedBadges badges={computeScholarshipBadges(s, null)} />
                      </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
